// @flow

import React, {useState, useEffect} from "react";
import {useObserver} from "mobx-react-lite";
import {Col, Row, Table, Badge, message} from "antd";
import {useHistory} from "react-router-dom";
import {
    PlusOutlined,
    FileSearchOutlined,
    BranchesOutlined,
    OrderedListOutlined,
    EnvironmentOutlined,
    CameraOutlined,
    DollarOutlined,
    SolutionOutlined,
    NotificationOutlined,
    BarcodeOutlined,
    ScissorOutlined,
    PushpinOutlined,
    SearchOutlined,
    ShoppingOutlined,
    AccountBookOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useStore} from "../../hooks/useStore";
import {useFetch} from "../../hooks/useFetch";
import {useCodPaymentBrandLabel} from "../../hooks/useCodPaymentBrandLabel";
import {activityColors} from "../../constants/constants";
import {ConditionalTooltip} from "../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {
    RightButton,
    FullWidthButton,
    FilterRow,
    ButtonRow,
    TableContainer
} from "./companyPageStyles";
import {CourierIcon} from "../_defaultComponents/CourierIcon/CourierIcon";
import {CompanySettingButton} from "../_defaultComponents/CompanySettingButton/CompanySettingButton";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import "moment-timezone";
import {
    CloseIcon,
    SearchInput,
    SelectField
} from "../_defaultComponents/FilterComponents/FilterComponentStyles";
import {EmptyMessage} from "../_defaultComponents/EmptyMessage/EmptyMessage";
import {CreditCardOutlined} from "@ant-design/icons";
const {Column} = Table;

export const CompanyPage = () => {
    const history = useHistory();
    const stores = useStore();
    const {t} = useTranslation(["companyPage"]);
    const isAgency = stores.meStore.checkRole("isAgency");
    const codPaymentBrandLabel = useCodPaymentBrandLabel();
    const [activity, setActivity] = useState(
        new URLSearchParams(history.location.search).get("activity") || "enabled"
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState(
        new URLSearchParams(history.location.search).get("searchText")
    );
    const [onboardingStatus, setOnboardingStatus] = useState(
        new URLSearchParams(history.location.search).get("onboardingStatus") || "all"
    );
    const [text, setText] = useState("");
    const [loading, data, setData, setLoading] = useFetch({
        fetchFn: stores.companyStore.getCompanyList.bind(null, {
            limit: 10,
            skip: (currentPage - 1) * 10,
            activity,
            onboardingStatus,
            searchText: searchText && searchText.toLocaleLowerCase("tr-TR")
        }),
        dependencies: [currentPage, searchText, activity, onboardingStatus]
    });

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(history.location.search);
        const urlPage = urlSearchParams.get("page");
        const urlActivity = urlSearchParams.get("activity");
        const urlOnboardingStatus = urlSearchParams.get("onboardingStatus");
        const urlSearchText = urlSearchParams.get("searchText");
        setTimeout(() => {
            if (urlPage) {
                const page = parseInt(urlPage);
                if (!isNaN(page)) setCurrentPage(page);
            }
            if (urlActivity) {
                setActivity(urlActivity);
            }
            if (urlOnboardingStatus) {
                setOnboardingStatus(urlOnboardingStatus);
            }
            if (urlSearchText) {
                setSearchText(urlSearchText);
                setText(urlSearchText);
            }
        });
    }, []);

    useEffect(() => {
        if (stores.meStore.checkRole("notVisibleForCompanyUser")) {
            setUrl();
        }
    }, [currentPage, activity, onboardingStatus, searchText]);

    const setUrl = () => {
        let path = history.location.pathname;
        if (currentPage) {
            path += "?page=" + currentPage;
        }
        if (activity) {
            path += "&activity=" + activity;
        }
        if (onboardingStatus) {
            path += "&onboardingStatus=" + onboardingStatus;
        }
        if (searchText) {
            path += "&searchText=" + searchText;
        }
        history.push(path);
    };

    const newCompany = async event => {
        event.preventDefault();
        await stores.appStore.setLoading(true);
        history.push("/companies/new");
        await stores.appStore.setLoading(false);
    };

    const renderConditionalTooltip = (
        textKey,
        uriKey,
        IconComponent,
        activityCondition,
        staticText = "",
        condition = true
    ) => {
        return (
            <Col>
                <ConditionalTooltip
                    condition={condition}
                    text={staticText || t(`detailButtons.${textKey}`, {ns: "companyPage"})}
                    child={
                        <CompanySettingButton
                            activityCondition={activityCondition}
                            href={"/companies/" + uriKey}
                            data-testid={`${textKey}Button`}
                            icon={<IconComponent />}
                        />
                    }
                />
            </Col>
        );
    };

    return useObserver(() => (
        <div data-testid="companyScreen">
            <Row
                type="flex"
                justify="space-between"
                data-testid="filterCard"
                style={{
                    width: "100%",
                    heigh: "100%"
                }}
            >
                <Col lg={24} xl={18} xxl={20}>
                    <FilterRow type="flex" gutter={[20, 5]} data-testid="filterCard">
                        <Col xs={24} sm={16} md={12} lg={8} xl={5}>
                            <SelectField
                                value={activity}
                                data-testid="activityFilter"
                                onChange={value => {
                                    setCurrentPage(1);
                                    setActivity(value);
                                }}
                            >
                                <SelectField.Option value="all" data-testid="activityFilter_option">
                                    {t("filters.all", {ns: "common"})}
                                </SelectField.Option>
                                <SelectField.Option
                                    value="enabled"
                                    data-testid="activityFilter_option"
                                >
                                    {t("activeCompanies", {ns: "companyPage"})}
                                </SelectField.Option>
                                <SelectField.Option
                                    value="disabled"
                                    data-testid="activityFilter_option"
                                >
                                    {t("inactiveCompanies", {ns: "companyPage"})}
                                </SelectField.Option>
                            </SelectField>
                        </Col>
                        <Col xs={24} sm={16} md={12} lg={8} xl={6}>
                            <SearchInput
                                placeholder={t("filters.search", {ns: "common"})}
                                value={text}
                                data-testid="searchInput"
                                onSearch={value => {
                                    setCurrentPage(1);
                                    if (value) {
                                        const reservedCharacters = /[!@#$&*(),?:\[\]]/g;
                                        if (!reservedCharacters.test(value)) {
                                            setSearchText(value);
                                        } else {
                                            message.error("Arama metni özel karakter içeremez");
                                        }
                                    }
                                }}
                                onChange={event => {
                                    setText(event.target.value);
                                }}
                                enterButton={
                                    <CustomButton
                                        data-testid="searchInput_searchButton"
                                        icon={<SearchOutlined />}
                                        style={{marginRight: "0px", marginTop: "0px"}}
                                    />
                                }
                                suffix={
                                    text ? (
                                        <CloseIcon
                                            data-testid="searchInput_deleteButton"
                                            onClick={() => {
                                                setCurrentPage(1);
                                                setSearchText("");
                                                setText("");
                                            }}
                                        />
                                    ) : (
                                        <span />
                                    )
                                }
                            />
                        </Col>
                    </FilterRow>
                </Col>

                {stores.meStore.checkRole("newCompany") && !isAgency && (
                    <ButtonRow gutter={[10, 10]}>
                        <Col>
                            <ConditionalTooltip
                                condition={
                                    !stores.appStore.companyUserList.filter(
                                        user => !user.company && !user.disabled
                                    ).length
                                }
                                text={"Hatalı Kayıtlar"}
                                child={
                                    <FullWidthButton
                                        href="/panel/companies/false-records"
                                        data-testid="newCompanyButton"
                                    >
                                        Hatalı Kayıtlar
                                    </FullWidthButton>
                                }
                            />
                        </Col>
                        <Col>
                            <ConditionalTooltip
                                condition={
                                    !stores.appStore.companyUserList.filter(
                                        user => !user.company && !user.disabled
                                    ).length
                                }
                                text={t("newCompanyTooltip", {ns: "companyPage"})}
                                child={
                                    <RightButton
                                        href="/companies/new"
                                        onClick={event => newCompany(event)}
                                        data-testid="newCompanyButton"
                                        icon={<PlusOutlined />}
                                    />
                                }
                            />
                        </Col>
                    </ButtonRow>
                )}
            </Row>
            <TableContainer
                loading={loading}
                dataSource={data.rows}
                rowKey="_id"
                data-testid="companyTable"
                pagination={{
                    total: data.dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                locale={{
                    emptyText: <EmptyMessage />
                }}
                scroll={{x: "max-content"}}
                display={data?.rows?.length > 0 ? "grid" : "flex"}
                dataTitles={[
                    t("fields.status", {ns: "common"}),
                    t("fields.companyName", {ns: "common"}),
                    t("fields.city", {ns: "common"}),
                    t("fields.county", {ns: "common"})
                ]}
            >
                <Column
                    dataIndex="disabled"
                    render={text => (
                        <Badge color={activityColors[!text]} data-testid="badge"></Badge>
                    )}
                />
                <Column
                    title={t("fields.companyName", {ns: "common"})}
                    dataIndex="companyName"
                    render={text => <span data-testid="companyName">{text}</span>}
                />
                <Column
                    title={t("fields.city", {ns: "common"})}
                    dataIndex="city"
                    render={text => <span data-testid="companyName">{text?.capitalize()}</span>}
                />
                <Column
                    title={t("fields.county", {ns: "common"})}
                    dataIndex="county"
                    render={text => <span data-testid="county">{text?.capitalize()}</span>}
                />
                <Column
                    title={t("actions.detail", {ns: "common"})}
                    dataIndex="_id"
                    width="40%"
                    render={(text, record) => (
                        <Row gutter={10}>
                            {renderConditionalTooltip(
                                "companyDetails",
                                text,
                                FileSearchOutlined,
                                false
                            )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.assignedCouriers") &&
                                renderConditionalTooltip(
                                    "assignedCouriers",
                                    `dedication/${text}`,
                                    CourierIcon,
                                    false
                                )}
                            {stores.meStore.checkRole("company.orderMerge") &&
                                stores.meStore.checkPermission("companies.orderMerge") &&
                                renderConditionalTooltip(
                                    "orderMerge",
                                    `merge-options/${text}`,
                                    BranchesOutlined,
                                    record.options?.merge.every(merge => merge.isActive)
                                        ? true
                                        : record.options?.merge.some(merge => merge.isActive)
                                        ? "some"
                                        : 0
                                )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.orderQueue") &&
                                renderConditionalTooltip(
                                    "orderQueue",
                                    `queue-options/${text}`,
                                    OrderedListOutlined,
                                    record.options?.queue?.isActive
                                )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.shiftOptions") &&
                                renderConditionalTooltip(
                                    "shiftOptions",
                                    `shift-options/${text}`,
                                    NotificationOutlined,
                                    record.options?.shift?.shiftControlIsActive
                                )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.proofOfDelivery") &&
                                renderConditionalTooltip(
                                    "proofOfDelivery",
                                    `proof-of-delivery/${text}`,
                                    CameraOutlined,
                                    record.options?.proof?.isActive
                                )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.smsTrackingOptions") &&
                                renderConditionalTooltip(
                                    "smsTrackingOptions",
                                    `sms-tracking-options/${text}`,
                                    EnvironmentOutlined,
                                    record.options?.smsTracking?.isActive
                                )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.dynamicPricing") &&
                                renderConditionalTooltip(
                                    "dynamicPricing",
                                    `dynamic-price-options/${text}`,
                                    DollarOutlined,
                                    record.options?.dynamicPrice?.isActive
                                )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.deliveryVerification") &&
                                renderConditionalTooltip(
                                    "deliveryVerification",
                                    `delivery-verification-options/${text}`,
                                    SolutionOutlined,
                                    record.options?.verification?.isActive
                                )}
                            {/* {renderConditionalTooltip(
                                "slotOrder",
                                `delivery-slot-options/${text}`,
                                FieldTimeOutlined,
                                record.options?.slot.every(slot => slot.isActive)
                                    ? true
                                    : record.options?.slot.some(slot => slot.isActive)
                                    ? "some"
                                    : 0
                            )} */}

                            {/* {renderConditionalTooltip(
                                "locationCheck",
                                `location-check-options/${text}`,
                                QuestionCircleOutlined,
                                record.options?.locationCheck?.isActive
                            )} */}
                            {!isAgency &&
                                stores.meStore.checkRole("company.shiftSuggestion") &&
                                renderConditionalTooltip(
                                    "shiftSuggestion",
                                    `shift-suggestion-options/${text}`,
                                    PushpinOutlined,
                                    record.options?.shiftSuggestion?.isActive
                                )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.barcodeOptions") &&
                                renderConditionalTooltip(
                                    "barcodeOptions",
                                    `barcode-options/${text}`,
                                    BarcodeOutlined,
                                    record.options?.barcode?.isActive
                                )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.cutOffOptions") &&
                                renderConditionalTooltip(
                                    "cutOffOptions",
                                    `cut-off/${text}`,
                                    ScissorOutlined,
                                    record.options?.cutOff?.isActive
                                )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.CODPaymentOptions") &&
                                renderConditionalTooltip(
                                    "CODPaymentOptions",
                                    `cod-payment-options/${text}`,
                                    CreditCardOutlined,
                                    false,
                                    `${codPaymentBrandLabel} Seçenekleri`
                                )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.readyOrderNotificationOptions") &&
                                renderConditionalTooltip(
                                    "readyOrderNotificationOptions",
                                    `ready-order-notification-options/${text}`,
                                    ShoppingOutlined,
                                    record.options?.readyOrderNotification?.isActive || false,
                                    "Hazır Sipariş Bildirimi Seçenekleri"
                                )}
                            {!isAgency &&
                                stores.meStore.checkRole("company.suppliers") &&
                                renderConditionalTooltip(
                                    "suppliers",
                                    `suppliers/${text}`,
                                    AccountBookOutlined,
                                    record.options?.readyOrderNotification?.isActive || false
                                )}
                        </Row>
                    )}
                />
            </TableContainer>
        </div>
    ));
};
